import InfiniteScroll from "react-infinite-scroll-component";
import { AccessLevel } from "@/api/williams/users";
import { DateTime, highlight } from "@/util";
import { ExpandableCard } from "../../components";
import { ExpirationsFilters, ExpirationsListView } from "@/api/williams/certificates";
import { faChevronDown, faChevronUp, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFullPath, routes } from "../../routes";
import { Link, useNavigate } from "react-router-dom";
import { useExpirationsList } from "../hooks";
import { usePermissions } from "../../users/hooks";
import { useState } from "react";

export type ExpirationsProps = {
  filters: Partial<ExpirationsFilters>;
  expanded?: boolean;
  allowExpand?: boolean;
};

export function Expirations({ filters, ...props }: ExpirationsProps) {
  const expirations = useExpirationsList(filters);

  const data = expirations.data?.pages.flatMap((page) => page.results) ?? [];
  const total = expirations.data?.pages[0]?.total ?? 0;

  return (
    <ExpandableCard caption={`Expirations (${total})`} expanded={props.expanded} allowExpand={props.allowExpand}>
      <InfiniteScroll
        dataLength={data.length}
        next={expirations.fetchNextPage}
        hasMore={Boolean(expirations.hasNextPage)}
        loader={<p>Loading...</p>}
      >
        {expirations.isLoading ? (
          <p>Loading...</p>
        ) : expirations.isError ? (
          <p>An unknown error occurred.</p>
        ) : expirations.isSuccess && data.length > 0 ? (
          <ExpirationsTable data={data} search={filters.searchString} />
        ) : filters.searchString ? (
          <p>Your search returned no results.</p>
        ) : (
          <p>No expirations found.</p>
        )}
      </InfiniteScroll>
    </ExpandableCard>
  );
}

function ExpirationsTable({ data, search }: { data: ExpirationsListView[]; search?: string }) {
  return (
    <table className="table table-sm table-borderless mb-0">
      <thead>
        <tr>
          <th className="col col-md-4">Welder</th>
          <th className="col-4 d-none d-md-table-cell">Standard</th>
          <th className="col-4 d-none d-md-table-cell">Process-technique</th>
          <th className="col-auto" />
        </tr>
      </thead>
      <tbody>
        {data.map((row, index) => (
          <ExpirationsRow key={index} row={row} search={search} />
        ))}
      </tbody>
    </table>
  );
}

function ExpirationsRow({ row, search }: { row: ExpirationsListView; search?: string }) {
  const [expanded, setExpanded] = useState<boolean>(false);

  const navigate = useNavigate();
  const permissions = usePermissions();

  function handleAddRenewal() {
    navigate(getFullPath(routes.welder.renewal), {
      state: {
        process: row.processName,
        technique: row.processType,
        welders: [{ welderId: row.welderId, name: row.welderName }],
      },
    });
  }

  return (
    <>
      <tr>
        <td>
          <Link to={getFullPath(routes.welder.open(row.welderId))} className="link link-primary">
            {highlight(row.welderName, search)}
          </Link>
        </td>
        <td className="d-none d-md-table-cell">{highlight(row.standard, search)}</td>
        <td className="d-none d-md-table-cell">{highlight(`${row.processName}-${row.processType}`, search)}</td>
        <td className="text-end text-nowrap">
          <FontAwesomeIcon
            role="button"
            icon={expanded ? faChevronUp : faChevronDown}
            size="sm"
            className="me-1"
            title="Details"
            onClick={() => setExpanded((prev) => !prev)}
            fixedWidth={true}
          />
          {permissions.data?.accessLevel !== AccessLevel.GeneralUser && (
            <FontAwesomeIcon
              role="button"
              icon={faUserPlus}
              size="sm"
              className="me-1"
              title="Add Renewal"
              onClick={handleAddRenewal}
              fixedWidth={true}
            />
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td className="d-none d-md-table-cell" colSpan={4}>
            <ExpandedDetails row={row} search={search} />
          </td>
          <td className="d-table-cell d-md-none" colSpan={2}>
            <ExpandedDetails row={row} search={search} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetails({ row, search }: { row: ExpirationsListView; search?: string }) {
  function getExpirationDate(date: string) {
    if (row.ineligible) {
      return "Ineligible";
    } else if (row.revoked) {
      return "Revoked";
    } else {
      return DateTime.getLocalDate(date);
    }
  }

  return (
    <div className="ms-1">
      <div className="bg-light p-1">Expiration Details</div>
      <div className="p-1">
        <dl>
          <>
            <dt className="d-block d-md-none">Standard</dt>
            <dd className="d-block d-md-none">{row.standard}</dd>
          </>
          <>
            <dt className="d-block d-md-none">Process-technique</dt>
            <dd className="d-block d-md-none">{highlight(`${row.processName}-${row.processType}`, search)}</dd>
          </>
          {row.contractor && (
            <>
              <dt>Contractor</dt>
              <dd>
                <Link to={getFullPath(routes.contractor.open(row.contractorId))} className="link link-primary">
                  {row.contractor}
                </Link>
              </dd>
            </>
          )}
          {row.cfR192 && (
            <>
              <dt>CFR 192</dt>
              <dd>{getExpirationDate(row.cfR192)}</dd>
            </>
          )}
          {row.cfR195 && (
            <>
              <dt>CFR 195</dt>
              <dd>{getExpirationDate(row.cfR195)}</dd>
            </>
          )}
          {row.basic && (
            <>
              <dt>Basic</dt>
              <dd>{getExpirationDate(row.basic)}</dd>
            </>
          )}
          {row.basicNde && (
            <>
              <dt>Basic + NDE</dt>
              <dd>{getExpirationDate(row.basicNde)}</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}
